import React from 'react';
import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Zap, ShieldCheck, TrendingDown, Plus, Trash2, LayoutDashboard, Settings2, AlertCircle, Lock as LockIcon, Unlock as UnlockIcon, Repeat, PlayCircle, Trophy, Flame } from "lucide-react";
import { DcaRound, PositionType } from '../types';
import { EXCHANGE_LIST, getSymbols, getCurrency, getMinNotionalWarning, getEnabledExchanges } from './dca/ExchangeUIConfigs';
import { NotificationSettings } from './dca/settings/NotificationSettings';

interface DetailedSettingsProps {
  selectedSymbol: string;
  setSelectedSymbol: (val: string) => void;
  positionType: PositionType;
  setPositionType: (val: PositionType) => void;
  leverage: number;
  setLeverage: (val: number) => void;
  takeProfit: number;
  setTakeProfit: (val: number) => void;
  dcaRounds: DcaRound[];
  addRound: () => void;
  removeRound: (id: number) => void;
  updateRound: (id: number, field: 'qty' | 'distance', value: number) => void;
  repeatExecution: boolean;
  setRepeatExecution: (val: boolean) => void;
  repeatType: string;
  setRepeatType: (val: string) => void;
  stopLoss: boolean;
  setStopLoss: (val: boolean) => void;
  stopLossValue: number;
  setStopLossValue: (val: number) => void;
  stopBotOnSL: boolean;
  setStopBotOnSL: (val: boolean) => void;
  useMaFilter: boolean;
  setUseMaFilter: (val: boolean) => void;
  useMa5Filter: boolean;
  setUseMa5Filter: (val: boolean) => void;
  useRsiEntry: boolean;
  setUseRsiEntry: (val: boolean) => void;
  useSuperMaFilter: boolean;
  setUseSuperMaFilter: (val: boolean) => void;
  useSuperMaBe: boolean;
  setUseSuperMaBe: (val: boolean) => void;
  useSuperMaScaling: boolean;
  setUseSuperMaScaling: (val: boolean) => void;
  useSuperMaSlowMode: boolean;
  setUseSuperMaSlowMode: (val: boolean) => void;
  superMaScoreThreshold: number;
  setSuperMaScoreThreshold: (val: number) => void;
  useStochAlignmentEntry: boolean;
  setUseStochAlignmentEntry: (val: boolean) => void;
  useRsiScaling: boolean;
  setUseRsiScaling: (val: boolean) => void;
  useTsEntry: boolean;
  setUseTsEntry: (val: boolean) => void;
  useTsScaling: boolean;
  setUseTsScaling: (val: boolean) => void;
  botName: string;
  setBotName: (val: string) => void;
  baseQty: number;
  setBaseQty: (val: number) => void;
  feePct: number;
  setFeePct: (val: number) => void;
  limitFeePct: number;
  setLimitFeePct: (val: number) => void;
  balance: number;
  setBalance: (val: number) => void;
  useAllBalance: boolean;
  setUseAllBalance: (val: boolean) => void;
  candleInterval: string;
  setCandleInterval: (val: string) => void;
  entryType: 'fixed' | 'percent';
  setEntryType: (val: 'fixed' | 'percent') => void;
  handleEntryTypeChange: (newType: 'fixed' | 'percent') => void;
  entryAmount: number;
  setEntryAmount: (val: number) => void;
  isAutoMargin: boolean;
  setIsAutoMargin: (val: boolean) => void;
  entryCount: number;
  setEntryCount: (val: number) => void;
  entryMultiplier: number;
  setEntryMultiplier: (val: number) => void;
  entryInterval: number;
  setEntryInterval: (val: number) => void;
  principalAmount: number;
  setPrincipalAmount: (val: number) => void;
  handleEntryAmountChange: (val: number) => void;
  isEntryFixed: boolean;
  setIsEntryFixed: (val: boolean) => void;
  marginMode: 'crossed' | 'isolated' | 'cross';
  setMarginMode: (val: 'crossed' | 'isolated' | 'cross') => void;
  isLive: boolean;
  slStrategy: 'realtime' | 'candle_close';
  setIsLive: (val: boolean) => void;
  setSlStrategy: (val: 'realtime' | 'candle_close') => void;
  setDcaRounds: (val: DcaRound[]) => void;
  tpThresholdStep: number;
  setTpThresholdStep: (val: number) => void;
  tpThresholdPct: number;
  setTpThresholdPct: (val: number) => void;
  useLimitTp: boolean;
  setUseLimitTp: (val: boolean) => void;
  pullbackPct: number;
  setPullbackPct: (val: number) => void;
  onStartBot: () => void;
  onSave?: () => void;
  is_testnet?: boolean;
  testnet_min_notional?: number;
  useTestnet: boolean;
  setUseTestnet: (val: boolean) => void;
  useProfitOffsetting: boolean;
  setUseProfitOffsetting: (val: boolean) => void;
  profitOffsetPct: number;
  setProfitOffsetPct: (val: number) => void;
  exchange?: string;
  setExchange?: (val: string) => void;
  suspendedSides: string[];
  setSuspendedSides: (val: string[]) => void;
  isEdit?: boolean;
  disable_all_testnet?: boolean;
  enabled_exchanges?: Record<string, boolean>;
  immediateReentry: boolean;
  setImmediateReentry: (val: boolean) => void;
  maPeriod: number;
  setMaPeriod: (val: number) => void;
  rsiThreshold: number;
  setRsiThreshold: (val: number) => void;
  use1hSidewaysFilter: boolean;
  setUse1hSidewaysFilter: (val: boolean) => void;
  use1hSidewaysScalingFilter: boolean;
  setUse1hSidewaysScalingFilter: (val: boolean) => void;
  adxThreshold: number;
  setAdxThreshold: (val: number) => void;
  use1hTrendFilter: boolean;
  setUse1hTrendFilter: (val: boolean) => void;
  use1hTrendSl: boolean;
  setUse1hTrendSl: (val: boolean) => void;
  use1hTrendBe: boolean;
  setUse1hTrendBe: (val: boolean) => void;
  applyPreset?: (type: 'hybrid' | 'martingale' | 'scalping') => void;
  telegramEnabled: boolean | null;
  setTelegramEnabled: (val: boolean | null) => void;
  telegramNotifyTypes: string[];
  setTelegramNotifyTypes: (val: string[]) => void;
}

export const DetailedSettings: React.FC<DetailedSettingsProps> = ({
  selectedSymbol, setSelectedSymbol,
  positionType, setPositionType,
  leverage, setLeverage,
  takeProfit, setTakeProfit,
  dcaRounds, addRound, removeRound, updateRound, setDcaRounds,
  repeatExecution, setRepeatExecution,
  repeatType, setRepeatType,
  stopLoss, setStopLoss,
  stopLossValue, setStopLossValue,
  stopBotOnSL, setStopBotOnSL,
  useMaFilter, setUseMaFilter,
  useMa5Filter, setUseMa5Filter,
  useRsiEntry, setUseRsiEntry,
  useSuperMaFilter, setUseSuperMaFilter,
  useSuperMaBe, setUseSuperMaBe,
  useSuperMaScaling, setUseSuperMaScaling,
  useSuperMaSlowMode, setUseSuperMaSlowMode,
  superMaScoreThreshold, setSuperMaScoreThreshold,
  useStochAlignmentEntry, setUseStochAlignmentEntry,
  useRsiScaling, setUseRsiScaling,
  useTsEntry, setUseTsEntry,
  useTsScaling, setUseTsScaling,
  botName, setBotName,
  baseQty, setBaseQty,
  feePct, setFeePct,
  limitFeePct, setLimitFeePct,
  balance, setBalance,
  useAllBalance, setUseAllBalance,
  candleInterval, setCandleInterval,
  entryType, setEntryType,
  handleEntryTypeChange,
  entryAmount, setEntryAmount,
  isAutoMargin, setIsAutoMargin,
  entryCount, setEntryCount,
  entryMultiplier, setEntryMultiplier,
  entryInterval, setEntryInterval,
  principalAmount, setPrincipalAmount,
  handleEntryAmountChange,
  isEntryFixed, setIsEntryFixed,
  marginMode, setMarginMode,
  isLive,
  setIsLive,
  slStrategy,
  setSlStrategy,
  tpThresholdStep,
  setTpThresholdStep,
  tpThresholdPct,
  setTpThresholdPct,
  use1hTrendFilter,
  setUse1hTrendFilter,
  use1hTrendSl,
  setUse1hTrendSl,
  use1hTrendBe,
  setUse1hTrendBe,
  useLimitTp,
  setUseLimitTp,
  pullbackPct,
  setPullbackPct,
  onStartBot,
  onSave,
  is_testnet,
  testnet_min_notional,
  useTestnet,
  setUseTestnet,
  useProfitOffsetting,
  setUseProfitOffsetting,
  profitOffsetPct,
  setProfitOffsetPct,
  exchange,
  setExchange,
  suspendedSides,
  setSuspendedSides,
  isEdit,
  disable_all_testnet,
  enabled_exchanges,
  immediateReentry,
  setImmediateReentry,
  maPeriod,
  setMaPeriod,
  rsiThreshold,
  setRsiThreshold,
  use1hSidewaysFilter,
  setUse1hSidewaysFilter,
  use1hSidewaysScalingFilter,
  setUse1hSidewaysScalingFilter,
  adxThreshold,
  setAdxThreshold,
  applyPreset,
  telegramEnabled,
  setTelegramEnabled,
  telegramNotifyTypes,
  setTelegramNotifyTypes
}) => {
  const currency = getCurrency(selectedSymbol);

  const baseEntryAmount = entryType === 'percent'
    ? (principalAmount > 0 ? principalAmount : (balance > 0 ? balance : 1)) * (baseQty / 100)
    : entryAmount;

  const currentRounds = isAutoMargin
    ? Array.from({ length: entryCount }).map((_, i) => ({
      qty: baseEntryAmount * Math.pow(entryMultiplier, i),
    }))
    : dcaRounds;

  const predictedTotalCost = currentRounds.reduce((acc, curr) => acc + curr.qty, 0);
  const totalRequiredPrincipal = isAutoMargin ? (isEntryFixed ? predictedTotalCost : principalAmount) : predictedTotalCost;
  const isInsufficientBalance = !useAllBalance && balance > 0 && balance < totalRequiredPrincipal;

  const firstRoundQty = Number(currentRounds[0]?.qty || entryAmount || 0);
  const firstEntryNotional = firstRoundQty * Number(leverage || 1);
  const minNotionalWarning = getMinNotionalWarning(exchange, isLive, is_testnet, firstEntryNotional, testnet_min_notional);

  // 현재 모드에 따른 렌더링용 라운드 데이터 (isAutoMargin일 때도 최신 계산값 보장)
  const roundsToRender = isAutoMargin
    ? Array.from({ length: entryCount }).map((_, i) => ({
      id: i + 1,
      qty: baseEntryAmount * Math.pow(entryMultiplier, i),
      distance: i === 0 ? 0 : entryInterval
    }))
    : dcaRounds;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start transition-colors duration-300">
      <div className="lg:col-span-8 flex flex-col gap-8">
        <Card className="bg-card shadow-sm border-border rounded-2xl overflow-hidden">
          {applyPreset && (
            <div className="bg-primary/5 border-b border-primary/10 p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black text-primary uppercase tracking-widest flex items-center gap-2">
                  <Zap className="w-3.5 h-3.5" /> 전략 프리셋 (Strategy Presets)
                </span>
                <Badge variant="outline" className="text-[8px] font-bold border-primary/30 text-primary">Quick Setup</Badge>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Button
                  variant="outline"
                  onClick={() => applyPreset('hybrid')}
                  className="h-14 flex flex-col items-center justify-center gap-1 border-indigo-500/20 hover:bg-indigo-500/10 hover:border-indigo-500/50 group transition-all"
                >
                  <span className="text-xs font-black text-indigo-600 group-hover:scale-110 transition-transform">🏆 슈퍼 하이브리드</span>
                  <span className="text-[8px] text-muted-foreground font-medium">안정적 수익 (권장)</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => applyPreset('martingale')}
                  className="h-14 flex flex-col items-center justify-center gap-1 border-amber-500/20 hover:bg-amber-500/10 hover:border-amber-500/50 group transition-all"
                >
                  <span className="text-xs font-black text-amber-600 group-hover:scale-110 transition-transform">🔥 클래식 마틴</span>
                  <span className="text-[8px] text-muted-foreground font-medium">공격적 물타기</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => applyPreset('scalping')}
                  className="h-14 flex flex-col items-center justify-center gap-1 border-emerald-500/20 hover:bg-emerald-500/10 hover:border-emerald-500/50 group transition-all"
                >
                  <span className="text-xs font-black text-emerald-600 group-hover:scale-110 transition-transform">⚡ 정밀 스캘핑</span>
                  <span className="text-[8px] text-muted-foreground font-medium">촘촘한 단타</span>
                </Button>
              </div>
            </div>
          )}
          <CardHeader className="bg-muted/30 border-b border-border p-0">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-8 py-6">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label className="text-[9px] font-bold text-muted-foreground uppercase px-1">거래소 (Exchange)</Label>
                  <Select value={exchange} onValueChange={setExchange}>
                    <SelectTrigger className="h-8 w-[200px] text-xs font-black border-border bg-background rounded-lg focus:ring-1 focus:ring-primary shadow-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {getEnabledExchanges(enabled_exchanges).map(ex => (
                        <SelectItem key={ex.value} value={ex.value}>{ex.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {exchange === 'okx' && (
                    <p className="text-[10px] font-bold text-amber-600 mt-1 animate-pulse px-1">
                      ⚠️ OKX는 현재 연동 테스트 중입니다. (시뮬레이션 권장)
                    </p>
                  )}
                  {exchange === 'bybit' && (
                    <p className="text-[10px] font-bold text-amber-600 mt-1 animate-pulse px-1">
                      ⚠️ Bybit는 현재 연동 테스트 중입니다. (시뮬레이션 권장)
                    </p>
                  )}
                  {/* {exchange === 'bitget' && (
                    <p className="text-[10px] font-bold text-amber-600 mt-1 animate-pulse px-1">
                      ⚠️ Bitget는 현재 연동 테스트 중입니다. (시뮬레이션 권장)
                    </p>
                  )} */}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label className="text-[9px] font-bold text-muted-foreground uppercase px-1">티커 심볼 (Symbol)</Label>
                  <Select value={selectedSymbol} onValueChange={setSelectedSymbol}>
                    <SelectTrigger className="h-8 w-[200px] text-xs font-black border-border bg-background rounded-lg focus:ring-1 focus:ring-primary shadow-sm">
                      <SelectValue placeholder="종목 선택" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border max-h-[50vh] overflow-y-auto">
                      {getSymbols(exchange, useTestnet).map(sym => (
                        <SelectItem key={sym.value} value={sym.value}>{sym.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex flex-col gap-1.5 mt-1.5">
                    <Label className="text-[9px] font-bold text-muted-foreground uppercase px-1">거래 모드 (Trading Mode)</Label>
                    <Select value={isLive ? 'live' : 'paper'} onValueChange={(v) => setIsLive(v === 'live')}>
                      <SelectTrigger className="h-7 w-[130px] text-[10px] font-black border-border bg-background rounded-lg focus:ring-1 focus:ring-primary shadow-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="paper">
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            <span className="text-[10px] font-bold">시뮬레이션 (Paper)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="live">
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                            <span className="text-[10px] font-bold">실매매 (Live)</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {(isLive && !disable_all_testnet && (exchange === 'binance' || exchange === 'okx' || exchange === 'bybit' || exchange === 'bitget')) && (
                  <div className="flex flex-col gap-1.5 mt-1.5">
                    <Label className="text-[9px] font-bold text-muted-foreground uppercase px-1">
                      접속 환경 ({exchange === 'binance' || exchange === 'bybit' ? 'Network' : 'Demo'})
                    </Label>
                    <Select value={useTestnet ? 'testnet' : 'mainnet'} onValueChange={(v) => setUseTestnet(v === 'testnet')} disabled={isEdit}>
                      <SelectTrigger className="h-7 w-[130px] text-[10px] font-black border-border bg-background rounded-lg focus:ring-1 focus:ring-primary shadow-sm disabled:opacity-80">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="mainnet">
                          <div className="flex items-center gap-2">
                            <ShieldCheck className="w-3 h-3 text-success" />
                            <span className="text-[10px] font-bold">메인넷 (Mainnet)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="testnet">
                          <div className="flex items-center gap-2">
                            <Zap className="w-3 h-3 text-amber-500" />
                            <span className="text-[10px] font-bold">
                              {exchange === 'binance' || exchange === 'bybit' ? '테스트넷 (Testnet)' : '데모 (Demo)'}
                            </span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex flex-col gap-1.5 mt-2 max-w-3xl">
                  <Label className="text-[10px] font-bold text-muted-foreground uppercase">전략 이름 (상세)</Label>
                  <Input
                    value={botName}
                    onChange={(e) => setBotName(e.target.value)}
                    placeholder="봇의 이름을 입력하세요"
                    className="h-8 text-sm font-black border-border rounded-lg focus:ring-1 focus:ring-primary bg-background"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <div className="flex flex-col gap-1.5 min-w-[100px]">
                    <Label className="text-[10px] font-bold text-muted-foreground uppercase">수수료 (Market %)</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={feePct}
                        step="0.001"
                        onChange={(e) => setFeePct(Number(e.target.value))}
                        className="h-8 text-sm font-black border-border rounded-lg pr-8 bg-background"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 font-bold text-muted-foreground text-[10px]">%</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 min-w-[100px]">
                    <Label className="text-[10px] font-bold text-muted-foreground uppercase">수수료 (Limit %)</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={limitFeePct}
                        step="0.001"
                        onChange={(e) => setLimitFeePct(Number(e.target.value))}
                        className="h-8 text-sm font-black border-border rounded-lg pr-8 bg-background"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 font-bold text-muted-foreground text-[10px]">%</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 min-w-[100px]">
                    <Label className="text-[10px] font-bold text-muted-foreground uppercase">캔들 간격 (Interval)</Label>
                    <Select value={candleInterval} onValueChange={setCandleInterval}>
                      <SelectTrigger className="h-8 text-sm font-black border-border rounded-lg focus:ring-1 focus:ring-primary bg-background">
                        <SelectValue placeholder="간격 선택" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="1m">1m</SelectItem>
                        <SelectItem value="3m">3m</SelectItem>
                        <SelectItem value="5m">5m</SelectItem>
                        <SelectItem value="15m">15m</SelectItem>
                        <SelectItem value="30m">30m</SelectItem>
                        <SelectItem value="1h">1시간봉 (1h)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="flex bg-muted/50 p-1 rounded-lg gap-1 w-full sm:w-[200px]">
                <Button
                  variant={positionType === 'LONG' ? "default" : "ghost"}
                  onClick={() => setPositionType('LONG')}
                  className={`flex-1 h-8 rounded-lg px-2 text-[11px] font-black transition-all ${positionType === 'LONG' ? 'bg-success hover:opacity-90 shadow-sm text-white' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Long
                </Button>
                <Button
                  variant={positionType === 'SHORT' ? "default" : "ghost"}
                  onClick={() => setPositionType('SHORT')}
                  className={`flex-1 h-8 rounded-lg px-2 text-[11px] font-black transition-all ${positionType === 'SHORT' ? 'bg-danger hover:opacity-90 shadow-sm text-white' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Short
                </Button>
                <Button
                  variant={positionType === 'BOTH' ? "default" : "ghost"}
                  onClick={() => setPositionType('BOTH')}
                  className={`flex-1 h-8 rounded-lg px-2 text-[11px] font-black transition-all ${positionType === 'BOTH' ? 'bg-primary hover:opacity-90 shadow-sm text-white' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Both
                </Button>
              </div>
              {positionType === 'BOTH' && (
                <div className="flex items-center gap-4 px-2 py-1 bg-muted/30 rounded-lg animate-in fade-in slide-in-from-right-2 duration-300">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="enable-long"
                      checked={!suspendedSides.includes('long')}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSuspendedSides(suspendedSides.filter(s => s !== 'long'));
                        } else {
                          setSuspendedSides([...suspendedSides, 'long']);
                        }
                      }}
                      className="border-success data-[state=checked]:bg-success data-[state=checked]:border-success"
                    />
                    <Label htmlFor="enable-long" className="text-[10px] font-black text-success uppercase cursor-pointer">Long 활성화</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="enable-short"
                      checked={!suspendedSides.includes('short')}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSuspendedSides(suspendedSides.filter(s => s !== 'short'));
                        } else {
                          setSuspendedSides([...suspendedSides, 'short']);
                        }
                      }}
                      className="border-danger data-[state=checked]:bg-danger data-[state=checked]:border-danger"
                    />
                    <Label htmlFor="enable-short" className="text-[10px] font-black text-danger uppercase cursor-pointer">Short 활성화</Label>
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="p-8 space-y-8">
              <div className="bg-muted/30 p-5 rounded-2xl border border-border flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3 bg-background px-4 py-2 rounded-xl border border-border shadow-sm">
                    <Checkbox
                      checked={useAllBalance}
                      onCheckedChange={(checked) => setUseAllBalance(!!checked)}
                      id="use-all-balance-detail"
                      className="border-border"
                    />
                    <Label htmlFor="use-all-balance-detail" className="text-sm font-bold cursor-pointer text-foreground">전체 계좌 자산 사용</Label>
                  </div>
                  {!useAllBalance && (
                    <div className="flex flex-col gap-1.5 animate-in slide-in-from-left-2 duration-200">
                      <Label className="text-[10px] font-bold text-muted-foreground uppercase px-1">최대 증거금 사용 제한 (Limit)</Label>
                      <div className="relative">
                        <Input
                          type="number"
                          value={balance}
                          onChange={(e) => setBalance(Number(e.target.value))}
                          className={`h-10 bg-background border-border rounded-xl text-right font-black pr-12 w-48 ${isInsufficientBalance ? 'border-danger ring-danger/10' : ''}`}
                          placeholder="금액 입력"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-muted-foreground">{currency}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-muted-foreground font-bold uppercase">사용 가능 자산:</span>
                    <span className="text-sm font-black text-foreground">--- {currency}</span>
                  </div>
                  {isInsufficientBalance && (
                    <p className="text-[10px] text-danger font-bold flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> 증거금 사용 제한({balance})이 목표 원금({totalRequiredPrincipal.toFixed(2)})보다 적습니다.
                    </p>
                  )}
                </div>
              </div>

              <div className="pt-4 space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-black text-foreground uppercase tracking-widest flex items-center gap-2">
                    <TrendingDown className="w-3.5 h-3.5 text-primary" /> 물타기(Scaling) 및 익절 전략
                  </h3>
                  <div className="flex bg-muted rounded-lg p-0.5 h-8 w-56">
                    <Button
                      variant={isAutoMargin ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setIsAutoMargin(true)}
                      className={`flex-1 h-full text-[10px] font-black rounded-md transition-all ${isAutoMargin ? 'bg-primary text-white shadow-sm' : 'text-muted-foreground'}`}
                    >
                      자동 계산 (Auto)
                    </Button>
                    <Button
                      variant={!isAutoMargin ? "default" : "ghost"}
                      size="sm"
                      onClick={() => {
                        // 직접 입력으로 전환 시 현재 계산된 사다리를 상태로 동기화 (유실 방지)
                        if (isAutoMargin) {
                          setDcaRounds(roundsToRender);
                        }
                        setIsAutoMargin(false);
                      }}
                      className={`flex-1 h-full text-[10px] font-black rounded-md transition-all ${!isAutoMargin ? 'bg-primary text-white shadow-sm' : 'text-muted-foreground'}`}
                    >
                      직접 입력 (Custom)
                    </Button>
                  </div>
                </div>

                <div className="bg-primary/5 p-4 rounded-xl border border-primary/10 mb-6">
                  <p className="text-[10px] font-black text-primary uppercase mb-2">설명</p>
                  <p className="text-[11px] text-muted-foreground font-medium leading-relaxed">
                    {isAutoMargin
                      ? "입력한 예산과 배수를 바탕으로 사다리를 자동으로 구성합니다. 초보자에게 추천합니다."
                      : "각 회사의 진입 시점과 수량을 개별적으로 설정할 수 있습니다. 숙련된 트레이더용 모드입니다."}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8 border-b border-dashed border-border pb-8">
                  <div className={`space-y-4 leading-none transition-all ${isAutoMargin ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}>
                    <Label className="text-muted-foreground font-semibold uppercase tracking-tight text-xs">전략 총 예산 (Total Budget)</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={principalAmount}
                        onChange={(e) => setPrincipalAmount(Number(e.target.value))}
                        step="0.01"
                        className={`h-10 rounded-lg border-border bg-background pr-12 font-black text-lg ${isInsufficientBalance ? 'border-danger ring-danger/10' : ''}`}
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-sm">{currency}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground font-medium leading-tight mt-2 ml-1">
                      * 이 예산은 물타기(Scaling) 단계별 진입 수량을 계산하기 위한 기준 금액입니다.<br />
                      * 실제 자산 운용은 상단의 <strong>[전체 계좌 자산 사용]</strong> 옵션에 따라 달라집니다.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="text-muted-foreground font-semibold uppercase tracking-tight text-xs">레버리지 (x{leverage})</Label>
                      <div className="flex bg-muted rounded-lg p-0.5 h-7">
                        <Button
                          variant={marginMode === 'crossed' || marginMode === 'cross' ? "default" : "ghost"}
                          size="sm"
                          onClick={() => setMarginMode('crossed')}
                          className={`px-2 h-full text-[9px] font-bold rounded-md ${marginMode === 'crossed' || marginMode === 'cross' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
                        >
                          교차 (Cross)
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled
                          className="px-2 h-full text-[9px] font-bold rounded-md text-muted-foreground/30 cursor-not-allowed bg-transparent opacity-40"
                        >
                          격리 (Isolated)
                        </Button>
                      </div>
                    </div>
                    <Slider value={[leverage]} max={100} step={1} onValueChange={(val) => setLeverage(val[0])} className="pt-2" />
                  </div>

                  <div className="space-y-4">
                    <Label className="text-muted-foreground font-semibold uppercase tracking-tight text-xs">익절 목표 (ROE %)</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={takeProfit}
                        onChange={(e) => setTakeProfit(Number(e.target.value))}
                        className="h-10 rounded-lg border-border bg-background pr-12 font-black text-lg text-primary"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-sm font-black">%</span>
                    </div>

                    <div className="flex items-center justify-between pt-1">
                      <Label className="text-[10px] font-black text-muted-foreground uppercase tracking-tight">지정가 익절 사용 (Limit TP)</Label>
                      <Checkbox
                        id="use_limit_tp"
                        checked={useLimitTp}
                        onCheckedChange={(val: any) => setUseLimitTp(!!val)}
                        className="border-primary/30 data-[state=checked]:bg-primary h-4 w-4"
                      />
                    </div>

                    {useLimitTp && (
                      <div className="space-y-1.5 pt-1 animate-in fade-in slide-in-from-top-1 duration-200">
                        <div className="flex justify-between items-center">
                          <Label className="text-[9px] font-bold text-muted-foreground uppercase">되돌림 방어 (%)</Label>
                          <span className="text-[10px] font-black text-primary">{pullbackPct}%</span>
                        </div>
                        <Input
                          type="number"
                          value={pullbackPct}
                          onChange={(e) => setPullbackPct(Number(e.target.value))}
                          step="0.01"
                          className="h-8 text-[11px] font-black bg-background border-border"
                          placeholder="0.1"
                        />
                      </div>
                    )}

                    <div className="pt-2 space-y-3 bg-primary/5 p-3 rounded-xl border border-primary/10">
                      <div className="flex items-center justify-between">
                        <Label className="text-[10px] font-black text-primary uppercase tracking-tight">단계별 가변 익절 (Dynamic TP)</Label>
                        <Badge variant="outline" className="text-[8px] font-bold h-4 bg-primary/10 border-primary/20 text-primary">Advanced</Badge>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 space-y-0">
                          <Label className="text-[10px] font-black text-muted-foreground/70 uppercase">적용 단계 (Step)</Label>
                          <Input
                            type="number"
                            value={tpThresholdStep || ''}
                            onChange={(e) => setTpThresholdStep(Number(e.target.value))}
                            placeholder="0 (비활성)"
                            className="h-7 text-[11px] font-black bg-background border-border"
                          />
                        </div>
                        <div className="flex-1 space-y-0">
                          <Label className="text-[10px] font-black text-muted-foreground/70 uppercase">변경 목표 (ROE %)</Label>
                          <Input
                            type="number"
                            value={tpThresholdPct || ''}
                            onChange={(e) => setTpThresholdPct(Number(e.target.value))}
                            placeholder="0.0"
                            step="0.1"
                            className="h-7 text-[11px] font-black bg-background border-border"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-top-2 duration-300">
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold text-muted-foreground uppercase">최대 단계 (Steps)</Label>
                  <Input
                    type="number"
                    value={isAutoMargin ? entryCount : dcaRounds.length}
                    onChange={(e) => setEntryCount(Number(e.target.value))}
                    className="h-9 bg-background border-border font-black disabled:opacity-70"
                    readOnly={!isAutoMargin}
                  />
                </div>
                {isAutoMargin && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-[10px] font-bold text-muted-foreground uppercase">수량 배수 (Multiplier)</Label>
                      <Input type="number" value={entryMultiplier} onChange={(e) => setEntryMultiplier(Number(e.target.value))} step="0.1" className="h-9 bg-background border-border font-black" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[10px] font-bold text-muted-foreground uppercase">물타기 간격 (%)</Label>
                      <Input type="number" value={entryInterval} onChange={(e) => setEntryInterval(Number(e.target.value))} step="0.01" className="h-9 bg-background border-border font-black" />
                    </div>
                  </>
                )}
              </div>

              <div className="flex flex-col gap-1.5 pt-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label className="text-[10px] text-muted-foreground font-bold uppercase">1회차 증거금 (또는 사용자산)</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEntryFixed(!isEntryFixed)}
                      className={`h-5 w-5 p-0 rounded-full ${isEntryFixed ? 'bg-amber-500/10 text-amber-500' : 'text-muted-foreground'}`}
                    >
                      {isEntryFixed ? <LockIcon className="w-3 h-3" /> : <UnlockIcon className="w-3 h-3" />}
                    </Button>
                  </div>
                  <div className="flex bg-muted rounded-md p-0.5 h-6">
                    <Button
                      variant={entryType === 'percent' ? "default" : "ghost"}
                      size="sm"
                      onClick={() => handleEntryTypeChange('percent')}
                      className={`px-1.5 h-full text-[9px] font-bold rounded ${entryType === 'percent' ? ' shadow-xs' : 'text-muted-foreground'}`}
                    >
                      %
                    </Button>
                    <Button
                      variant={entryType === 'fixed' ? "default" : "ghost"}
                      size="sm"
                      onClick={() => handleEntryTypeChange('fixed')}
                      className={`px-1.5 h-full text-[9px] font-bold rounded ${entryType === 'fixed' ? ' shadow-xs' : 'text-muted-foreground'}`}
                    >
                      $
                    </Button>
                  </div>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    value={entryType === 'percent' ? baseQty : entryAmount}
                    onChange={(e) => handleEntryAmountChange(Number(e.target.value))}
                    step="0.01"
                    className={`flex h-9 w-full bg-background border border-border rounded-lg px-3 py-1 text-sm font-black transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary pr-12 ${isEntryFixed ? 'ring-1 ring-amber-500/30' : ''}`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-muted-foreground">
                    {entryType === 'percent' ? '%' : currency}
                  </span>
                </div>
                {entryType === 'percent' && principalAmount > 0 && (
                  <p className="text-[10px] text-primary font-bold ml-1 mt-1">
                    예상 진입 금액: {(principalAmount * (baseQty / 100)).toFixed(2)} {currency}
                  </p>
                )}
              </div>


              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="font-bold text-foreground flex items-center gap-2">
                    <LayoutDashboard className="w-4 h-4 text-primary" />{isAutoMargin ? '진입 회차별 사다리 미리보기 (자동 계산)' : '진입 회차별 상세 설정 (직접 입력)'}
                  </h3>
                  {!isAutoMargin && (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          const synced = Array.from({ length: entryCount }).map((_, i) => ({
                            id: i + 1,
                            qty: i === 0 ? (entryType === 'percent' ? baseQty : entryAmount) : (entryType === 'percent' ? baseQty : entryAmount) * Math.pow(entryMultiplier, i),
                            distance: i === 0 ? 0 : entryInterval
                          }));
                          setDcaRounds(synced);
                        }}
                        variant="outline" size="sm" className="h-8 border-success/20 text-success font-bold px-3"
                      >
                        <TrendingDown className="w-3.5 h-3.5 mr-1" />자동 계산값 불러오기
                      </Button>
                      <Button onClick={addRound} variant="outline" size="sm" className="h-8 border-primary/20 text-primary font-bold px-3"><Plus className="w-3.5 h-3.5 mr-1" />회차 추가</Button>
                    </div>
                  )}
                </div>
                <div className={`rounded-xl border border-border overflow-hidden bg-card ${isAutoMargin ? 'opacity-80' : ''}`}>
                  <Table>
                    <TableHeader className="bg-muted/30">
                      <TableRow className="border-border">
                        <TableHead className="w-[80px] font-black text-[10px] uppercase text-muted-foreground">회차</TableHead>
                        <TableHead className="font-black text-[10px] uppercase text-muted-foreground text-center">진입 기준 (%)</TableHead>
                        <TableHead className="font-black text-[10px] uppercase text-muted-foreground text-center">평단 변화 (%)</TableHead>
                        <TableHead className="font-black text-[10px] uppercase text-muted-foreground">진입 증거금</TableHead>
                        <TableHead className="font-black text-[10px] uppercase text-muted-foreground">진입 간격 (%)</TableHead>
                        {!isAutoMargin && <TableHead className="w-[60px]"></TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        let cumulativeDist = 0;
                        let totalQty = 0;
                        let totalNotional = 0;

                        return roundsToRender.map((round, idx) => {
                          if (idx > 0) cumulativeDist += Number(round.distance);

                          // 평단가 계산 시뮬레이션 (최초 진입가를 100으로 가정)
                          const currentPriceWeight = positionType === 'SHORT' ? (100 + cumulativeDist) : (100 - cumulativeDist);
                          totalQty += Number(round.qty);
                          totalNotional += (Number(round.qty) * currentPriceWeight);
                          const avgPriceWeight = totalNotional / totalQty;
                          const avgDropPct = Math.abs(100 - avgPriceWeight);

                          const displayDistance = cumulativeDist === 0 ? "0.00" : (positionType === 'BOTH' ? `±${cumulativeDist.toFixed(2)}` : (positionType === 'LONG' ? `-${cumulativeDist.toFixed(2)}` : `+${cumulativeDist.toFixed(2)}`));

                          return (
                            <TableRow key={round.id} className="border-border">
                              <TableCell className="font-black text-muted-foreground/40 text-[11px]"># {round.id}</TableCell>
                              <TableCell className="text-center">
                                <div className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-black ${cumulativeDist === 0 ? 'bg-muted text-muted-foreground' : (positionType === 'BOTH' ? 'bg-primary text-primary-foreground' : (positionType === 'LONG' ? 'bg-danger text-danger-foreground' : 'bg-success text-success-foreground'))}`}>
                                  {displayDistance}%
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <div className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-black ${idx === 0 ? 'bg-muted text-muted-foreground' : 'bg-amber-500/10 text-amber-600'}`}>
                                  {idx === 0 ? '-' : `${(positionType === 'SHORT' ? '+' : '-')}${avgDropPct.toFixed(2)}%`}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="relative max-w-[120px]">
                                  <Input type="number" value={round.qty.toFixed(2)} onChange={(e) => updateRound(round.id, 'qty', Number(e.target.value))} className="h-8 text-xs font-black pr-8" disabled={isAutoMargin} />
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-black">$</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="relative max-w-[120px]">
                                  <Input type="number" value={round.distance} onChange={(e) => updateRound(round.id, 'distance', Number(e.target.value))} step="0.1" className="h-8 text-xs font-black pr-8" disabled={isAutoMargin || idx === 0} />
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-black">%</span>
                                </div>
                              </TableCell>
                              {!isAutoMargin && (
                                <TableCell className="text-center">
                                  <Button onClick={() => removeRound(round.id)} variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-danger" disabled={idx === 0}><Trash2 className="w-4 h-4" /></Button>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        });
                      })()}

                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="bg-muted/10 p-6 rounded-2xl border border-border space-y-6 mt-8">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-5 h-5 text-amber-500 fill-amber-500" /><h3 className="text-xl font-black text-foreground">주문 상세 옵션</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {positionType === 'BOTH' && (
                    <div className="flex flex-col gap-2 p-4 rounded-xl bg-muted/30 border border-border animate-in fade-in slide-in-from-top-1 duration-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Checkbox checked={useProfitOffsetting} onCheckedChange={(checked) => setUseProfitOffsetting(!!checked)} id="use-offset-detail" className="border-border" />
                          <div className="flex flex-col">
                            <Label htmlFor="use-offset-detail" className="text-xs font-bold cursor-pointer text-foreground flex items-center gap-2">
                              <ShieldCheck className="w-4 h-4 text-primary" />수익 상계 처리 (Offsetting)
                            </Label>
                            <p className="text-[10px] text-muted-foreground font-medium mt-0.5">익절 시 반대편 손실 포지션을 함께 정리합니다.</p>
                          </div>
                        </div>
                        {useProfitOffsetting && (
                          <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-1 duration-200">
                            <span className="text-[10px] font-bold text-muted-foreground uppercase">비율</span>
                            <div className="relative w-20">
                              <Input
                                type="number"
                                value={profitOffsetPct}
                                onChange={(e) => setProfitOffsetPct(Number(e.target.value))}
                                className="h-8 text-xs font-black pr-6 rounded-lg border-border bg-background"
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-muted-foreground">%</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col gap-2 p-4 rounded-xl bg-muted/30 border border-border">
                    <div className="flex items-center justify-between">
                      <Select value={useLimitTp ? 'limit' : 'market'} onValueChange={(val) => setUseLimitTp(val === 'limit')} disabled={!repeatExecution}>
                        <SelectTrigger className="w-[120px] h-8 text-xs rounded-lg border-border bg-background"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="market">시장가 익절</SelectItem>
                          <SelectItem value="limit">지정가 익절</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1 px-1 leading-tight">
                      * 현재 진입 및 물타기 주문은 시장가로만 수행되며, 지정가 옵션은 익절(Take Profit) 주문 시에만 적용됩니다.
                    </p>
                  </div>

                  <div className="flex flex-col gap-2 p-4 rounded-xl bg-muted/30 border border-border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Checkbox checked={immediateReentry} onCheckedChange={(checked) => setImmediateReentry(!!checked)} id="immediate-reentry-detail" className="border-border" />
                        <div className="flex flex-col">
                          <Label htmlFor="immediate-reentry-detail" className="text-xs font-bold cursor-pointer text-foreground flex items-center gap-2">
                            <Zap className="w-4 h-4 text-amber-500 fill-amber-500" />익절 후 즉시 재진입
                          </Label>
                          <p className="text-[10px] text-muted-foreground font-medium mt-0.5">익절 후 지표 조건(RSI 등)을 무시하고 즉시 다음 사이클을 시작합니다.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-border">
                      <div className="flex items-center gap-3">
                        <Checkbox checked={stopLoss} onCheckedChange={(checked) => setStopLoss(!!checked)} id="sl-detail" className="border-border" />
                        <div className="flex flex-col">
                          <Label htmlFor="sl-detail" className="font-bold cursor-pointer flex items-center gap-2 text-foreground"><TrendingDown className="w-4 h-4 text-danger" />손절 사용</Label>
                          <p className="text-[10px] text-muted-foreground font-medium mt-0.5">설정한 ROE 손실률에 도달하면 포지션을 종료합니다.</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground font-bold tracking-tight uppercase">ROE</span>
                        <div className="relative w-24">
                          <Input type="number" value={stopLossValue} onChange={(e) => setStopLossValue(Number(e.target.value))} disabled={!stopLoss} className="h-8 text-xs text-right pr-6 rounded-lg font-black border-border bg-background" />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground font-bold">%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {stopLoss && (
                    <div className="flex flex-col mt-4 animate-in fade-in slide-in-from-top-1 duration-300">
                      <div className="flex items-center gap-2 mb-2">
                        <Label className="font-bold text-xs text-muted-foreground uppercase tracking-wider">손절 타이밍 (Strategy)</Label>
                      </div>
                      <div className="grid grid-cols-2 bg-muted/50 p-1 rounded-xl gap-1 w-full border border-border/50">
                        <Button
                          variant={slStrategy === 'realtime' ? 'default' : 'ghost'}
                          size="sm"
                          onClick={() => setSlStrategy('realtime')}
                          className={`h-9 rounded-lg px-2 text-[10px] sm:text-xs font-bold transition-all ${slStrategy === 'realtime' ? 'bg-primary text-primary-foreground shadow-lg' : 'text-muted-foreground hover:bg-muted font-medium'}`}
                        >
                          실시간 (즉시)
                        </Button>
                        <Button
                          variant={slStrategy === 'candle_close' ? 'default' : 'ghost'}
                          size="sm"
                          onClick={() => setSlStrategy('candle_close')}
                          className={`h-9 rounded-lg px-2 text-[10px] sm:text-xs font-bold transition-all ${slStrategy === 'candle_close' ? 'bg-success text-white shadow-lg' : 'text-muted-foreground hover:bg-muted font-medium'}`}
                        >
                          봉 마감 (휩소방지)
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div className="flex items-center justify-between p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={useMaFilter} onCheckedChange={(checked) => setUseMaFilter(!!checked)} id="ma-filter" className="border-border" />
                      <div className="flex flex-col">
                        <Label htmlFor="ma-filter" className="font-bold cursor-pointer text-blue-500">이동평균선(MA) 추세 필터 사용</Label>
                        <p className="text-[9px] text-blue-500/70 font-medium mt-0.5">가격이 MA(실제로는 EMA사용) 위에 있으면 롱, 아래에 있으면 숏 진입을 시도합니다. (설정된 캔들 타임프레임 기준)</p>
                      </div>
                    </div>
                    {useMaFilter && (
                      <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-1 duration-200">
                        <span className="text-[10px] font-bold text-blue-500/60 uppercase">Period</span>
                        <Input
                          type="number"
                          value={maPeriod}
                          onChange={(e) => setMaPeriod(Number(e.target.value))}
                          className="h-8 w-20 text-xs font-black rounded-lg border-blue-500/20 bg-background"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={useMa5Filter} onCheckedChange={(checked) => setUseMa5Filter(!!checked)} id="ma5-filter" className="border-border" />
                      <div className="flex flex-col">
                        <Label htmlFor="ma5-filter" className="font-bold cursor-pointer text-blue-500">5MA 이격 필터 사용</Label>
                        <p className="text-[9px] text-blue-500/70 font-medium mt-0.5">상승중(롱)일 때는 5일선 + 0.1% 이하, 하락중(숏)일 때는 5일선 - 0.1% 이상에서만 진입합니다.</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={useRsiEntry} onCheckedChange={(checked) => setUseRsiEntry(!!checked)} id="rsi-entry" className="border-border" />
                      <div className="flex flex-col">
                        <Label htmlFor="rsi-entry" className="font-bold cursor-pointer text-indigo-500">RSI 모멘텀 필터 (진입)</Label>
                        <p className="text-[9px] text-indigo-500/70 font-medium mt-0.5">롱은 RSI {rsiThreshold} 이하, 숏은 {100 - rsiThreshold} 이상일 때만 진입을 시작합니다. (선택한 분봉[캔들간격] 기준)</p>
                      </div>
                    </div>
                    {(useRsiEntry || useRsiScaling) && (
                      <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-1 duration-200">
                        <span className="text-[10px] font-bold text-indigo-500/60 uppercase">Threshold</span>
                        <Input
                          type="number"
                          value={rsiThreshold}
                          onChange={(e) => setRsiThreshold(Number(e.target.value))}
                          className="h-8 w-20 text-xs font-black rounded-lg border-indigo-500/20 bg-background"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={useRsiScaling} onCheckedChange={(checked) => setUseRsiScaling(!!checked)} id="rsi-scaling" className="border-border" />
                      <div className="flex flex-col">
                        <Label htmlFor="rsi-scaling" className="font-bold cursor-pointer text-indigo-500">RSI 모멘텀 필터 (물타기)</Label>
                        <p className="text-[9px] text-indigo-500/70 font-medium mt-0.5">물타기 시점에서도 RSI 임계값 조건을 만족할 때까지 대기합니다.</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl bg-danger/10 border border-danger/20">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={useTsEntry} onCheckedChange={(checked) => setUseTsEntry(!!checked)} id="ts-entry" className="border-border" />
                      <div className="flex flex-col">
                        <Label htmlFor="ts-entry" className="font-bold cursor-pointer text-danger">추세 억제 (진입)</Label>
                        <p className="text-[9px] text-danger/70 font-medium mt-0.5">급격한 추세 시(EMA 이격 및 스토캐스틱 과매수/도) 진입을 제한합니다.</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl bg-orange-500/10 border border-orange-500/20">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={useTsScaling} onCheckedChange={(checked) => setUseTsScaling(!!checked)} id="ts-scaling" className="border-border" />
                      <div className="flex flex-col">
                        <Label htmlFor="ts-scaling" className="font-bold cursor-pointer text-orange-500">추세 억제 (물타기)</Label>
                        <p className="text-[9px] text-orange-500/70 font-medium mt-0.5">강한 원웨이 추세 중에는 물타기를 멈추고 진정될 때까지 대기합니다.</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={use1hTrendFilter} onCheckedChange={(checked) => setUse1hTrendFilter(!!checked)} id="1h-trend-filter" className="border-border" />
                      <div className="flex flex-col">
                        <Label htmlFor="1h-trend-filter" className="font-bold cursor-pointer text-amber-500">1시간봉 역배열 필터 (1h EMA Trend Filter)</Label>
                        <p className="text-[9px] text-amber-500/70 font-medium mt-0.5">1시간봉 20, 60, 120 EMA가 역배열인 경우 신규 진입 및 물타기를 중단합니다.</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl bg-danger/10 border border-danger/20">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={use1hTrendSl} onCheckedChange={(checked) => setUse1hTrendSl(!!checked)} id="1h-trend-sl" className="border-border" />
                      <div className="flex flex-col">
                        <Label htmlFor="1h-trend-sl" className="font-bold cursor-pointer text-danger">1시간봉 추세 반전 손절 (1h Trend Reverse SL)</Label>
                        <p className="text-[9px] text-danger/70 font-medium mt-0.5">포지션 보유 중 1시간봉이 역배열로 전환되면 즉시 시장가로 청산합니다.</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={use1hTrendBe} onCheckedChange={(checked) => setUse1hTrendBe(!!checked)} id="1h-trend-be" className="border-border" />
                      <div className="flex flex-col">
                        <Label htmlFor="1h-trend-be" className="font-bold cursor-pointer text-indigo-500">1시간봉 본전 탈출 모드 (1h Trend Break-even)</Label>
                        <p className="text-[9px] text-indigo-500/70 font-medium mt-0.5">역배열 시 물타기를 중단하고 익절가를 본전 수준으로 낮추어 탈출을 우선합니다.</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-4 p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20 col-span-1 md:col-span-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Checkbox checked={useSuperMaFilter} onCheckedChange={(checked) => setUseSuperMaFilter(!!checked)} id="super-ma-filter" className="border-border" />
                        <div className="flex flex-col">
                          <Label htmlFor="super-ma-filter" className="font-bold cursor-pointer text-indigo-600">슈퍼 이평선 필터 (Super MA Macro Filter)</Label>
                          <p className="text-[9px] text-indigo-600/70 font-medium mt-0.5">4시간봉 EMA 정렬 점수(0~5)를 기반으로 거시적 하락장에서 진입을 차단합니다.</p>
                        </div>
                      </div>
                      {useSuperMaFilter && (
                        <div className="flex flex-col gap-3 ml-7 pb-2 animate-in fade-in slide-in-from-top-1 duration-200">
                          <div className="flex items-center gap-3">
                            <Checkbox checked={useSuperMaScaling} onCheckedChange={(checked) => setUseSuperMaScaling(!!checked)} id="super-ma-scaling" className="border-indigo-400" />
                            <div className="flex flex-col">
                              <Label htmlFor="super-ma-scaling" className="text-[10px] font-bold cursor-pointer text-indigo-500">진입 비중 조절 (Entry Scaling)</Label>
                              <p className="text-[8px] text-indigo-400/80 font-medium">점수가 낮을 때 첫 진입 금액을 60~80%로 축소하여 시작합니다.</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Checkbox checked={useSuperMaSlowMode} onCheckedChange={(checked) => setUseSuperMaSlowMode(!!checked)} id="super-ma-slow" className="border-indigo-400" />
                            <div className="flex flex-col">
                              <Label htmlFor="super-ma-slow" className="text-[10px] font-bold cursor-pointer text-indigo-500">물타기 거리/배수 조절 (Slow Mode)</Label>
                              <p className="text-[8px] text-indigo-400/80 font-medium">하락장 점수가 낮을 때 물타기를 더 멀리, 적게 하여 시드를 아낍니다.</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Checkbox checked={useSuperMaBe} onCheckedChange={(checked) => setUseSuperMaBe(!!checked)} id="super-ma-be" className="border-indigo-400" />
                            <div className="flex flex-col">
                              <Label htmlFor="super-ma-be" className="text-[10px] font-bold cursor-pointer text-indigo-500">슈퍼 본전 탈출 모드 (Breakeven Exit)</Label>
                              <p className="text-[8px] text-indigo-400/80 font-medium text-amber-600/80">점수가 2점 미만(추세 반전)일 때 익절가를 본전으로 낮춰 탈출합니다.</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] font-bold text-indigo-600/60 uppercase">Threshold Score</span>
                            <Input
                              type="number"
                              min="0"
                              max="5"
                              value={superMaScoreThreshold}
                              onChange={(e) => setSuperMaScoreThreshold(Number(e.target.value))}
                              className="h-8 w-20 text-xs font-black rounded-lg border-indigo-500/20 bg-background text-center"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <Checkbox checked={useStochAlignmentEntry} onCheckedChange={(checked) => setUseStochAlignmentEntry(!!checked)} id="stoch-alignment" className="border-border" />
                      <div className="flex flex-col">
                        <Label htmlFor="stoch-alignment" className="font-bold cursor-pointer text-indigo-600">스토캐스틱 정렬 진입 (Stoch Alignment)</Label>
                        <p className="text-[9px] text-indigo-600/70 font-medium mt-0.5">15분봉/5분봉 파동이 정렬되었을 때만 진입하여 신뢰도를 높입니다.</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={use1hSidewaysFilter} onCheckedChange={(checked) => setUse1hSidewaysFilter(!!checked)} id="1h-sideways-filter" className="border-border" />
                      <div className="flex flex-col">
                        <Label htmlFor="1h-sideways-filter" className="font-bold cursor-pointer text-emerald-600">1시간봉 횡보 장세 필터 (1h Sideways Filter)</Label>
                        <p className="text-[9px] text-emerald-600/70 font-medium mt-0.5">ADX 지표를 사용하여 횡보 시에만 진입을 허용하고 강한 추세 시(ADX &gt; 임계값) 진입을 억제합니다.</p>
                      </div>
                    </div>
                    {use1hSidewaysFilter && (
                      <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-1 duration-200">
                        <span className="text-[10px] font-bold text-emerald-600/60 uppercase">ADX Max</span>
                        <Input
                          type="number"
                          value={adxThreshold}
                          onChange={(e) => setAdxThreshold(Number(e.target.value))}
                          className="h-8 w-20 text-xs font-black rounded-lg border-emerald-500/20 bg-background"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={use1hSidewaysScalingFilter} onCheckedChange={(checked) => setUse1hSidewaysScalingFilter(!!checked)} id="1h-sideways-scaling-filter" className="border-border" />
                      <div className="flex flex-col">
                        <Label htmlFor="1h-sideways-scaling-filter" className="font-bold cursor-pointer text-emerald-600">1시간봉 횡보 물타기 필터 (1h Sideways Scaling Filter)</Label>
                        <p className="text-[9px] text-emerald-600/70 font-medium mt-0.5">이미 포지션이 있는 상태에서도 ADX가 임계값을 넘으면 물타기를 중단하고 대기합니다.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Telegram Notification Settings */}
              <NotificationSettings
                telegramEnabled={telegramEnabled}
                setTelegramEnabled={setTelegramEnabled}
                telegramNotifyTypes={telegramNotifyTypes}
                setTelegramNotifyTypes={setTelegramNotifyTypes}
                isLive={isLive}
              />

              <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 pt-4 border-t border-border/50">
                <div className="flex-1 w-full flex items-center justify-between p-4 rounded-xl bg-muted/20 border border-border/50">
                  <div className="flex items-center gap-3">
                    <Checkbox checked={stopBotOnSL} onCheckedChange={(checked) => setStopBotOnSL(!!checked)} id="stop-bot-detail" className="border-border" />
                    <Label htmlFor="stop-bot-detail" className="text-sm text-foreground font-bold cursor-pointer">손절 주문 시 봇 정지</Label>
                  </div>
                </div>
                <div className={`flex flex-col gap-4 rounded-2xl p-6 border shadow-sm relative transition-all duration-500 overflow-hidden ${predictedTotalCost < (principalAmount * 0.95) ? 'bg-amber-500/5 border-amber-500/20' :
                  predictedTotalCost > (principalAmount * 1.05) ? 'bg-danger/5 border-danger/20' :
                    'bg-card border-border'
                  }`}>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">예상 총 투자 금액 (Cost)</p>
                      <div className="flex items-center gap-2">
                        {predictedTotalCost > principalAmount && (
                          <span className="text-[9px] font-bold text-danger animate-in fade-in slide-in-from-left-1 duration-300">
                            ⚠️ 예산 초과
                          </span>
                        )}
                        <Badge variant="outline" className="text-[10px] font-black">사용률 {(predictedTotalCost / (principalAmount || 1) * 100).toFixed(1)}%</Badge>
                      </div>
                    </div>
                    <p className="text-3xl font-black">{predictedTotalCost.toFixed(2)} <span className="text-sm font-bold opacity-70">{currency}</span></p>
                  </div>
                  <div className="flex flex-col gap-1 pt-3 border-t border-border/20">
                    <p className="text-[10px] text-primary font-bold uppercase tracking-wider">실제 총 진입 규모 (Margin)</p>
                    <p className="text-xl font-bold text-muted-foreground">{(predictedTotalCost * leverage).toFixed(2)} <span className="text-sm font-bold opacity-70">{currency}</span></p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
          <CardFooter className="bg-muted/30 p-6 flex flex-col sm:flex-row gap-4 border-t border-border">
            <Button onClick={onSave} variant="outline" className="w-full sm:flex-1 py-7 rounded-2xl border-2 border-border font-black text-foreground">전략 저장</Button>
            <Button
              onClick={onStartBot}
              disabled={isInsufficientBalance}
              className={`w-full sm:flex-1 py-7 rounded-2xl font-black shadow-xl ${isInsufficientBalance ? 'bg-muted text-muted-foreground' : 'bg-primary text-primary-foreground hover:opacity-90'}`}
            >
              {isInsufficientBalance ? '원금 부족으로 생성 불가' : '전문가 모드 봇 생성'}
            </Button>
          </CardFooter>
        </Card>
      </div>

      <div className="lg:col-span-4 space-y-6">
        <div className="bg-card rounded-2xl p-6 border border-border shadow-sm space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Settings2 className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-bold">도움말 및 팁</h3>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">상세 모드에서는 각 회차의 진입 시점과 수량을 개별적으로 설정하여 더욱 정밀한 거미줄 매매 전략을 구사할 수 있습니다.</p>
          <div className="space-y-3 pt-2">
            {[
              { q: "레버리지 조절", a: "고배율은 청산 위험이 큽니다. 격리 마진을 권장합니다." },
              { q: "누적 진입 기준", a: "최초 진입가 대비 전체 하락폭을 미리 확인하며 리스크를 관리하세요." },
            ].map((item, idx) => (
              <div key={idx} className="space-y-1">
                <p className="text-xs font-bold text-foreground">{item.q}</p>
                <p className="text-[11px] text-muted-foreground leading-normal">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
